import React from "react";
import { Edit } from "@refinedev/mui";
import {
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Alert,
  Divider,
} from "@mui/material";
import { useForm } from "@refinedev/react-hook-form";
import { Controller } from "react-hook-form";
import { IResourceComponentsProps } from "@refinedev/core";
import { format } from "date-fns";

interface IPremiumDeviceEdit {
  id: number;
  macAddress: string;
  serialNumber?: string;
  description?: string;
  validityDays: number;
  isActive: boolean;
  activatedAt?: Date;
  activatedByUserId?: number;
  user?: {
    id: number;
    email: string;
    fullname?: string;
  };
}

export const PremiumDeviceEdit: React.FC<IResourceComponentsProps> = () => {
  const {
    saveButtonProps,
    refineCore: { queryResult, formLoading },
    register,
    control,
    formState: { errors },
  } = useForm<IPremiumDeviceEdit>();

  const deviceData = queryResult?.data?.data;

  return (
    <Edit isLoading={formLoading} saveButtonProps={saveButtonProps}>
      <Box
        component="form"
        sx={{ display: "flex", flexDirection: "column", gap: 3 }}
        autoComplete="off"
      >
        {deviceData?.activatedByUserId && (
          <Alert severity="warning">
            <Typography variant="body2">
              This device is already activated by a user. Modifying it may affect the user's premium status.
            </Typography>
          </Alert>
        )}

        {/* Device Information (Read-only) */}
        <Box>
          <Typography variant="h6" gutterBottom>
            Device Information
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, ml: 2 }}>
            <Typography variant="body2">
              <strong>MAC Address:</strong> {deviceData?.macAddress || "N/A"}
            </Typography>
            <Typography variant="body2">
              <strong>Serial Number:</strong> {deviceData?.serialNumber || "N/A"}
            </Typography>
            {deviceData?.activatedAt && (
              <Typography variant="body2">
                <strong>Activated At:</strong>{" "}
                {format(new Date(deviceData.activatedAt), "yyyy-MM-dd HH:mm:ss")}
              </Typography>
            )}
            {deviceData?.user && (
              <Typography variant="body2">
                <strong>Activated By:</strong>{" "}
                {deviceData.user.fullname || deviceData.user.email}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider />

        {/* Editable Fields */}
        <Typography variant="h6" gutterBottom>
          Configuration
        </Typography>

        <TextField
          {...register("description")}
          error={!!errors.description}
          helperText={errors.description?.message as string}
          margin="normal"
          fullWidth
          label="Batch Description"
          placeholder="e.g., Commercial Contract Q1 2025"
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          {...register("validityDays", {
            required: "This field is required",
            min: { value: 1, message: "Minimum 1 day" },
            max: { value: 3650, message: "Maximum 3650 days (10 years)" },
            valueAsNumber: true,
          })}
          error={!!errors.validityDays}
          helperText={
            (errors.validityDays?.message as string) ||
            "Duration of premium access in days when this device is activated"
          }
          margin="normal"
          fullWidth
          label="Validity Duration (days)"
          type="number"
          InputProps={{
            inputProps: { min: 1, max: 3650 },
          }}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          {...register("serialNumber")}
          error={!!errors.serialNumber}
          helperText={errors.serialNumber?.message as string}
          margin="normal"
          fullWidth
          label="Serial Number (Optional)"
          placeholder="e.g., SN-123456"
          InputLabelProps={{ shrink: true }}
        />

        <Controller
          control={control}
          name="isActive"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  {...field}
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Device Active</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Only active devices can be used to activate premium
                  </Typography>
                </Box>
              }
            />
          )}
        />

        {!deviceData?.isActive && (
          <Alert severity="info">
            <Typography variant="body2">
              This device is currently inactive and cannot be used to activate premium.
            </Typography>
          </Alert>
        )}
      </Box>
    </Edit>
  );
};
